/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation
} from '@angular/core';
/**
 * An internal class that represents the data corresponding to a single calendar cell.
 * \@docs-private
 */
export class MdCalendarCell {
/**
 * @param {?} value
 * @param {?} displayValue
 * @param {?} ariaLabel
 * @param {?} enabled
 */
constructor(public value: number,
public displayValue: string,
public ariaLabel: string,
public enabled: boolean) {}
}

function MdCalendarCell_tsickle_Closure_declarations() {
/** @type {?} */
MdCalendarCell.prototype.value;
/** @type {?} */
MdCalendarCell.prototype.displayValue;
/** @type {?} */
MdCalendarCell.prototype.ariaLabel;
/** @type {?} */
MdCalendarCell.prototype.enabled;
}

/**
 * An internal component used to display calendar data in a table.
 * \@docs-private
 */
export class MdCalendarBody {
/**
 * The label for the table. (e.g. "Jan 2017").
 */
label: string;
/**
 * The cells to display in the table.
 */
rows: MdCalendarCell[][];
/**
 * The value in the table that corresponds to today.
 */
todayValue: number;
/**
 * The value in the table that is currently selected.
 */
selectedValue: number;
/**
 * The minimum number of free cells needed to fit the label in the first row.
 */
labelMinRequiredCells: number;
/**
 * The number of columns in the table.
 */
numCols = 7;
/**
 * Whether to allow selection of disabled cells.
 */
allowDisabledSelection = false;
/**
 * The cell number of the active cell in the table.
 */
activeCell = 0;
/**
 * Emits when a new value is selected.
 */
selectedValueChange = new EventEmitter<number>();
/**
 * @param {?} cell
 * @return {?}
 */
_cellClicked(cell: MdCalendarCell): void {
    if (!this.allowDisabledSelection && !cell.enabled) {
      return;
    }
    this.selectedValueChange.emit(cell.value);
  }
/**
 * The number of blank cells to put at the beginning for the first row.
 * @return {?}
 */
get _firstRowOffset(): number {
    return this.rows && this.rows.length && this.rows[0].length ?
        this.numCols - this.rows[0].length : 0;
  }
/**
 * @param {?} rowIndex
 * @param {?} colIndex
 * @return {?}
 */
_isActiveCell(rowIndex: number, colIndex: number): boolean {
    let /** @type {?} */ cellNumber = rowIndex * this.numCols + colIndex;

    // Account for the fact that the first row may not have as many cells.
    if (rowIndex) {
      cellNumber -= this._firstRowOffset;
    }

    return cellNumber == this.activeCell;
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: '[md-calendar-body]',
  templateUrl: 'calendar-body.html',
  styleUrls: ['calendar-body.css'],
  host: {
    'class': 'mat-calendar-body',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'label': [{ type: Input },],
'rows': [{ type: Input },],
'todayValue': [{ type: Input },],
'selectedValue': [{ type: Input },],
'labelMinRequiredCells': [{ type: Input },],
'numCols': [{ type: Input },],
'allowDisabledSelection': [{ type: Input },],
'activeCell': [{ type: Input },],
'selectedValueChange': [{ type: Output },],
};
}

function MdCalendarBody_tsickle_Closure_declarations() {
/** @type {?} */
MdCalendarBody.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdCalendarBody.ctorParameters;
/** @type {?} */
MdCalendarBody.propDecorators;
/**
 * The label for the table. (e.g. "Jan 2017").
 * @type {?}
 */
MdCalendarBody.prototype.label;
/**
 * The cells to display in the table.
 * @type {?}
 */
MdCalendarBody.prototype.rows;
/**
 * The value in the table that corresponds to today.
 * @type {?}
 */
MdCalendarBody.prototype.todayValue;
/**
 * The value in the table that is currently selected.
 * @type {?}
 */
MdCalendarBody.prototype.selectedValue;
/**
 * The minimum number of free cells needed to fit the label in the first row.
 * @type {?}
 */
MdCalendarBody.prototype.labelMinRequiredCells;
/**
 * The number of columns in the table.
 * @type {?}
 */
MdCalendarBody.prototype.numCols;
/**
 * Whether to allow selection of disabled cells.
 * @type {?}
 */
MdCalendarBody.prototype.allowDisabledSelection;
/**
 * The cell number of the active cell in the table.
 * @type {?}
 */
MdCalendarBody.prototype.activeCell;
/**
 * Emits when a new value is selected.
 * @type {?}
 */
MdCalendarBody.prototype.selectedValueChange;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
